import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { agencyReportsGet } from '@/store/agencyReports';
import { downloadReport } from '@/store/reports';
import { demoAgencyReports, demoAgencyAggregated, demoReports } from '@/demoData';
import { useDebounce } from '@/hooks/useDebounce';

import AppPageContainer from '@/components/AppPageContainer';
import AppBox from '@/components/AppBox';
import AppInput from '@/components/AppInput';
import AppDropdown from '@/components/AppDropdown';
import AgencyUsersReportsTable from '@/components/Tables/AgencyUsersReportsTable';
import AppPagination from '@/components/Tables/AppPagination';
import AppModal from '@/components/AppModal';
import ReportConfirmModal from '@/components/Modals/ReportConfirmModal';
import AppText from '@/components/AppText';
import AppLoader from '@/components/AppLoader';

const AgencyReports: React.FC = () => {
  const dispatch = useAppDispatch();
  const { me } = useAppSelector((state) => state.auth);
  const isDemo = me?.id === 207;
  const {
    reports,
    aggregated,
    weeks,
    current_week,
    pagination,
    loading,
  } = useAppSelector((state) => state.agencyReports);

  const [week, setWeek] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isOpen, setIsOpen] = useState(false);
  const [reportIdToDownload, setReportIdToDownload] = useState<number | null>(null);
  const [loadingReports, setLoadingReports] = useState<{ [key: number]: boolean }>({});

  const debouncedSearch = useDebounce(inputValue, 1000);

  // Fetch reports when params change
  useEffect(() => {
    if (isDemo) return;
    if (!me?.agency?.id) return;
    dispatch(
      agencyReportsGet({
        agencyId: me.agency.id,
        week: week || undefined,
        page,
        perPage: rowsPerPage,
        search: debouncedSearch || undefined,
      })
    );
  }, [dispatch, me?.agency?.id, week, page, rowsPerPage, debouncedSearch, isDemo]);

  // Initialise selected week after first load
  useEffect(() => {
    if (weeks.length && !week) {
      setWeek(current_week || weeks[0]);
    }
  }, [weeks, current_week, week]);

  // override data for demo
  const displayReports = isDemo ? demoAgencyReports : reports;
  const displayAggregated = isDemo ? demoAgencyAggregated : aggregated;

  const displayWeeks = isDemo ? demoReports.map((r: {week:string})=>r.week) : weeks;

  const handleDownload = async () => {
    if (!reportIdToDownload) return;
    setLoadingReports((prev) => ({ ...prev, [reportIdToDownload]: true }));
    try {
      await dispatch(downloadReport(reportIdToDownload)).unwrap();
    } catch (e) {
      console.error('Failed to download report', e);
    } finally {
      setTimeout(() => {
        setLoadingReports((prev) => ({ ...prev, [reportIdToDownload]: false }));
      }, 10000);
      setReportIdToDownload(null);
      setIsOpen(false);
    }
  };

  const summaryBox = (label: string, value: number) => (
    <AppBox column align="center" className="px-4 py-2 bg-gray-100 rounded min-w-[140px]">
      <AppText className="font-semibold whitespace-nowrap text-center">{label}</AppText>
      <AppText>{value}</AppText>
    </AppBox>
  );

  const summarySkeleton = () => (
    <AppBox gap={4} className="mb-4 flex-wrap">
      {[...Array(5)].map((_, idx) => (
        <AppBox
          key={idx}
          width={140}
          height={64}
          className="bg-gray-100 rounded animate-pulse"
        />
      ))}
    </AppBox>
  );

  return (
    <AppPageContainer
      title="Agency Reports"
      titleAdorment={
        <AppBox align="center" gap={4}>
          {/* Week dropdown */}
          <AppDropdown
            label={week || 'Select week'}
            items={displayWeeks.map((w) => ({ label: w, value: w }))}
            value={week || ''}
            onChange={(val) => {
              setWeek(val as string);
              setPage(1);
            }}
          />
          {/* Search */}
          <AppInput
            placeholder="Search users"
            value={inputValue}
            onChange={(v) => setInputValue(v)}
            className="w-[320px]"
          />
        </AppBox>
      }
    >
      {loading ? (
        summarySkeleton()
      ) : displayAggregated ? (
        <AppBox gap={4} className="mb-4 flex-wrap">
          {summaryBox('Total Leads', displayAggregated.total)}
          {summaryBox('Message Leads', displayAggregated.message)}
          {summaryBox('Message Leads Responded', displayAggregated.message_responded)}
          {summaryBox('Missed Calls', displayAggregated.missed_call)}
          {summaryBox('Answered Calls', displayAggregated.answered_call)}
        </AppBox>
      ) : null}

      {loading ? (
        <AppBox className="flex justify-center items-center h-full">
          <AppLoader size={40} />
        </AppBox>
      ) : (
        <AgencyUsersReportsTable
          tableRows={displayReports}
          onDownload={(id) => {
            if (id && !loadingReports[id]) {
              setReportIdToDownload(id);
              setIsOpen(true);
            }
          }}
          loadingReports={loadingReports}
        />
      )}

      {pagination && pagination.total > rowsPerPage && (
        <AppPagination
          totalRows={pagination.total}
          rowsPerPage={rowsPerPage}
          currentPage={page - 1}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onRowsPerPageChange={(newRows) => {
            setRowsPerPage(newRows);
            setPage(1);
          }}
        />
      )}

      <AppModal isOpen={isOpen} title="Confirm Report Download" onClose={() => setIsOpen(false)}>
        <ReportConfirmModal onConfirm={handleDownload} onClose={() => setIsOpen(false)} />
      </AppModal>
    </AppPageContainer>
  );
};

export default AgencyReports; 